import React, { useEffect, useRef, useState } from 'react';
import { Upload, Trash2, Link as LinkIcon, Loader2, File as FileIcon, Search } from 'lucide-react';
import { adminApi, absoluteMediaUrl } from './api';
import { useLanguage } from '../context/LanguageContext';
import { ADMIN_T } from './labels';

const formatBytes = (n) => {
  if (!n) return '0 KB';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const AdminMedia = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = ADMIN_T[language] || ADMIN_T.en;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get('/media', { params: search ? { search } : {} });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const id = setTimeout(fetchItems, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line
  }, [search]);

  const handleUpload = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    setUploading(true);
    try {
      await adminApi.post('/media', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      await fetchItems();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    await adminApi.delete(`/media/${id}`);
    setItems((p) => p.filter((x) => x.id !== id));
  };

  const copyUrl = (url) => {
    navigator.clipboard?.writeText(absoluteMediaUrl(url));
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  return (
    <div className="space-y-6">
      <div className={`flex flex-wrap items-center gap-3 ${isAr ? 'flex-row-reverse' : ''}`}>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white flex-1">{t.media}</h1>
        <input ref={inputRef} type="file" accept="image/*,application/pdf" hidden onChange={(e) => handleUpload(e.target.files?.[0])} />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="admin-media-upload"
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-brand-blue to-brand-blue-700 text-white text-sm font-semibold shadow disabled:opacity-60 ${isAr ? 'flex-row-reverse' : ''}`}
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {t.uploadFile}
        </button>
      </div>

      {/* Search */}
      <div className={`relative ${isAr ? 'flex-row-reverse' : ''}`}>
        <Search className={`absolute top-3 ${isAr ? 'right-3' : 'left-3'} w-4 h-4 text-slate-400`} />
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t.search}
          className={`w-full md:max-w-sm ${isAr ? 'pr-10 pl-3 text-right' : 'pl-10 pr-3 text-left'} py-2.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm`}
        />
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-20">{t.noData}</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {items.map((m) => (
            <div key={m.id} className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
              <div className="aspect-square bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                {m.mime_type.startsWith('image/') ? (
                  <img src={absoluteMediaUrl(m.url)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <FileIcon className="w-10 h-10 text-slate-400" />
                )}
              </div>
              <div className={`p-3 ${isAr ? 'text-right' : 'text-left'}`}>
                <p className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">{m.original_name}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{formatBytes(m.size)}</p>
                <div className={`flex items-center gap-2 mt-2 ${isAr ? 'flex-row-reverse' : ''}`}>
                  <button type="button" onClick={() => copyUrl(m.url)} title={t.copyUrl} className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center hover:bg-brand-blue/10 hover:text-brand-blue-700">
                    <LinkIcon className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => handleDelete(m.id)} className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-700">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Copied toast */}
      <div className={`fixed bottom-8 left-1/2 z-[200] pointer-events-none transition-all duration-300
                        ${copied ? 'opacity-100 -translate-x-1/2 translate-y-0' : 'opacity-0 -translate-x-1/2 translate-y-4'}`}>
        <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-slate-900 text-white text-sm font-semibold shadow-xl">
          {t.linkCopied}
        </div>
      </div>
    </div>
  );
};

export default AdminMedia;
